import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportProposalDetails from "hooks/organization/reports/types/useGetReportProposalDetails";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import { SortDirection } from "generated/graphql";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableProposalDetails(
  startDate,
  endDate,
  companyId,
  estimatorId,
  jobId,
  productType,
  status,
  wonAt,
  columns
) {
  const [getReportProposalDetails, { data, loading, error }] = useGetReportProposalDetails(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        companyId,
        estimatorId,
        jobId,
        productTypeId: productType,
        status,
        wonAt,
        page,
        first,
      };
      getReportProposalDetails({ variables });
    },
    [
      startDate,
      endDate,
      companyId,
      estimatorId,
      jobId,
      productType,
      status,
      wonAt,
      getReportProposalDetails,
    ]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportProposalDetails?.paginatorInfo,
  });

  const tableData = useMemo(() => {
    return {
      columns: [
        { Header: "Cost basis", accessor: "cost_basis", visible: true },
        ...columns.filter((column) => column.visible),
      ],
      rows: data?.getReportProposalDetails?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    getReportProposalDetails,
    pagination,
    metadata: data?.getReportProposalDetails?.metadata,
  } as const;
}
